import { createContext, useContext, useState, type ReactNode } from "react"
import type { AuthState, User } from "../types"

const API_URL = import.meta.env.VITE_API_URL || "/api"

interface AuthContextType {
  auth: AuthState
  login: (username: string, password: string) => Promise<void>
  register: (username: string, email: string, password: string) => Promise<void>
  logout: () => void
  resendVerification: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>(() => {
    const token = localStorage.getItem("token")
    const userJson = localStorage.getItem("user")
    const user = userJson ? (JSON.parse(userJson) as User) : null
    return { token, user, isAuthenticated: !!token }
  })

  const login = async (username: string, password: string) => {
    const formData = new URLSearchParams()
    formData.append("username", username)
    formData.append("password", password)

    const res = await fetch(`${API_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: formData,
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || "Login failed")
    }

    const data = await res.json()
    const token = data.access_token

    const meRes = await fetch(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const user = await meRes.json()

    localStorage.setItem("token", token)
    localStorage.setItem("user", JSON.stringify(user))
    setAuth({ token, user, isAuthenticated: true })
  }

  const register = async (username: string, email: string, password: string) => {
    const res = await fetch(`${API_URL}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, email, password }),
    })

    if (!res.ok) {
      const text = await res.text().catch(() => "")
      let detail = "Registration failed"
      try {
        const err = JSON.parse(text)
        detail = err.detail || `Error ${res.status}: ${text.slice(0, 200)}`
      } catch {
        detail = `Error ${res.status}: ${text.slice(0, 200)}`
      }
      throw new Error(detail)
    }

    await login(username, password)
  }

  const logout = () => {
    localStorage.removeItem("token")
    localStorage.removeItem("user")
    setAuth({ token: null, user: null, isAuthenticated: false })
  }

  const resendVerification = async () => {
    if (!auth.token) throw new Error("Not authenticated")
    const res = await fetch(`${API_URL}/auth/resend-verification`, {
      method: "POST",
      headers: { Authorization: `Bearer ${auth.token}` },
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || "Failed to resend verification email")
    }
  }

  const refreshUser = async () => {
    if (!auth.token) return
    const res = await fetch(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${auth.token}` },
    })
    if (res.ok) {
      const user = await res.json()
      localStorage.setItem("user", JSON.stringify(user))
      setAuth((prev) => ({ ...prev, user }))
    }
  }

  return (
    <AuthContext.Provider value={{ auth, login, register, logout, resendVerification, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
